import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

interface MockDataWrapperProps {
  children: React.ReactNode
  className?: string
  message?: string
}

export function MockDataWrapper({
  children,
  className,
  message = "Coming soon — this is sample data",
}: MockDataWrapperProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "cursor-help text-amber-600 dark:text-amber-400",
            className,
          )}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="bg-amber-950 text-amber-50 dark:bg-amber-100 dark:text-amber-950">
        {message}
      </TooltipContent>
    </Tooltip>
  )
}

interface MockTextBadgeProps {
  children: React.ReactNode
  label?: string
  message?: string
  className?: string
}

export function MockTextBadge({
  children,
  label = "preview",
  message = "Coming soon — this is sample data",
  className,
}: MockTextBadgeProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex cursor-help items-center gap-2 rounded-md border border-dashed border-amber-400/60 bg-amber-50/80 px-2.5 py-1 dark:border-amber-600/40 dark:bg-amber-950/40",
            className,
          )}
        >
          <span className="text-amber-700/70 dark:text-amber-400/70">
            {children}
          </span>
          <span className="rounded bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-amber-700 uppercase dark:bg-amber-800/60 dark:text-amber-300">
            {label}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="bg-amber-950 text-amber-50 dark:bg-amber-100 dark:text-amber-950">
        {message}
      </TooltipContent>
    </Tooltip>
  )
}

interface MockBadgeProps {
  children: React.ReactNode
  message?: string
  className?: string
}

export function MockBadge({
  children,
  message = "Coming soon — this is sample data",
  className,
}: MockBadgeProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "relative inline-flex cursor-help items-center",
            className,
          )}
        >
          {/* Faded badge content */}
          <span className="opacity-50 grayscale-[20%]">{children}</span>
          {/* Diagonal "preview" ribbon effect */}
          <span className="absolute -top-1 -right-1 rounded-full bg-amber-400 px-1.5 py-0.5 text-[8px] font-bold text-amber-950 uppercase shadow-sm dark:bg-amber-500">
            soon
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="bg-amber-950 text-amber-50 dark:bg-amber-100 dark:text-amber-950">
        {message}
      </TooltipContent>
    </Tooltip>
  )
}

interface MockCardOverlayProps {
  children: React.ReactNode
  message?: string
}

export function MockCardOverlay({
  children,
  message = "Coming soon",
}: MockCardOverlayProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative cursor-help">
          {/* Faded content */}
          <div className="pointer-events-none opacity-50 grayscale-[30%] select-none">
            {children}
          </div>
          {/* Overlay gradient */}
          <div className="absolute inset-0 rounded-b-lg bg-gradient-to-t from-amber-100/80 via-transparent to-transparent dark:from-amber-950/60" />
          {/* Coming soon badge */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800 shadow-sm dark:bg-amber-900/80 dark:text-amber-200">
              {message}
            </span>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="bg-amber-950 text-amber-50 dark:bg-amber-100 dark:text-amber-950">
        This feature is under development
      </TooltipContent>
    </Tooltip>
  )
}
